import { useMemo, useState } from "react";
import { Select, Tag } from "../ui";
import { useAcademicYears } from "../../hooks/useAcademicYears";
import { KINDS, STATES, destinationOf, formatKigaliDate, stateOf, useStaffScholarships } from "./model";
import CreateScholarshipDialog from "./CreateScholarshipDialog";
import CallsPage, { setupColumn } from "../calls/CallsPage";
import ScholarshipSetupDialog from "./ScholarshipSetupDialog";

const PAGE_SIZE = 20;

// Sponsored scholarships, or abroad or private-institution loan calls (`kind`): the Scholarship Admin creates one, then sets it
// up in one dialog; the DG and the Minister open the same dialog read-only
export default function ScholarshipsPage({ kind = KINDS.scholarship }) {
  const [filters, setFilters] = useState({ state: "", academicYearId: "" });
  const [page, setPage] = useState(0);
  const [creating, setCreating] = useState(false);
  const [setupId, setSetupId] = useState(null);

  const { data, isPending, isError } = useStaffScholarships({ ...filters, type: kind.type, page, size: PAGE_SIZE });
  const { data: academicYears = [] } = useAcademicYears({ includeFuture: true });
  const yearOptions = useMemo(() => academicYears.map((y) => ({ value: y.id, label: y.academicYearName })), [academicYears]);

  const setFilter = (name) => (e) => {
    setFilters((prev) => ({ ...prev, [name]: e.target.value }));
    setPage(0);
  };

  const columns = [
    { key: "name", label: kind.isLoan ? "Loan call" : "Scholarship", render: (s) => <span className="font-medium text-slate-800">{s.name}</span> },
    ...(kind === KINDS.scholarship ? [{ key: "sponsor", label: "Partner", accessor: (s) => s.sponsor?.name ?? "—" }] : []),
    { key: "country", label: "Country", accessor: (s) => destinationOf(s) },
    { key: "academicYear", label: "Academic year", accessor: (s) => s.academicYear?.name ?? "—" },
    { key: "opensAt", label: "Opens", accessor: (s) => formatKigaliDate(s.opensAt) },
    { key: "closesAt", label: "Closes", accessor: (s) => formatKigaliDate(s.closedAt ?? s.closesAt), sortValue: (s) => s.closedAt ?? s.closesAt },
    { key: "submittedApplications", label: "Applicants", accessor: (s) => s.submittedApplications ?? 0 },
    {
      key: "state",
      label: "State",
      accessor: (s) => stateOf(s.state).label,
      render: (s) => <Tag color={stateOf(s.state).color}>{stateOf(s.state).label}</Tag>,
    },
    setupColumn(setSetupId),
  ];

  return (
    <CallsPage
      title={kind.title}
      subtitle={data ? `${data.totalElements} ${kind.many}` : null}
      newLabel={`New ${kind.one}`}
      onNew={() => setCreating(true)}
      table={{
        columns,
        data: data?.content ?? [],
        isLoading: isPending,
        exportName: kind.listPath.slice(1).replaceAll("/", "-"),
        emptyTitle: isError ? `Could not load the ${kind.many}. Please try again later.` : `No ${kind.many} found`,
        pagination: { page, totalPages: data?.totalPages ?? 0, totalElements: data?.totalElements, pageSize: PAGE_SIZE, onPageChange: setPage },
        filters: (
          <>
            <Select aria-label="State" placeholder="All states" value={filters.state} onChange={setFilter("state")} options={STATES} className="w-full sm:w-48" />
            <Select
              aria-label="Academic year"
              placeholder="All academic years"
              value={filters.academicYearId}
              onChange={setFilter("academicYearId")}
              options={yearOptions}
              className="w-full sm:w-56"
            />
          </>
        ),
      }}
    >
      <CreateScholarshipDialog
        kind={kind}
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(scholarship) => {
          setCreating(false);
          setSetupId(scholarship.id);
        }}
      />
      {/* Keyed so each scholarship starts on its first tab */}
      <ScholarshipSetupDialog key={setupId} scholarshipId={setupId} onClose={() => setSetupId(null)} />
    </CallsPage>
  );
}
